import { useCallback, useMemo } from 'react';

import {
    useBioscopeRestartController_BioscopeRestarts,
    useBioscopeRestartController_BioscopeRouterRestarts,
    useBioscopeRestartController_RestartBioscope,
    useBioscopeRestartController_RestartRouter,
} from '@/services/hooks';

import { toBioscopeRestart, toBioscopeRouterRestart } from './mappers';
import type { BioscopeRestarts } from './types';

/** How often a list is refetched while one of its restarts is still running. */
const RUNNING_POLL_MS = 2000;

function pollWhileRunning(query: {
    state: { data: { data: { isDone: boolean }[] } | undefined };
}): number | false {
    return query.state.data?.data.some((restart) => !restart.isDone) ? RUNNING_POLL_MS : false;
}

/** The bioscope's cabinet and router restart history, and the two SMS restart actions. */
export function useBioscopeRestarts(bioscopeId: number, enabled: boolean): BioscopeRestarts {
    const restartsQuery = useBioscopeRestartController_BioscopeRestarts(bioscopeId, {
        enabled,
        refetchInterval: pollWhileRunning,
    });
    const routerRestartsQuery = useBioscopeRestartController_BioscopeRouterRestarts(bioscopeId, {
        enabled,
        refetchInterval: pollWhileRunning,
    });

    const { refetch: refetchRestarts } = restartsQuery;
    const { refetch: refetchRouterRestarts } = routerRestartsQuery;
    const refetch = useCallback(() => {
        refetchRestarts();
        refetchRouterRestarts();
    }, [refetchRestarts, refetchRouterRestarts]);

    const { mutateAsync: restartBioscope } = useBioscopeRestartController_RestartBioscope({
        onSettled: refetch,
    });
    const { mutateAsync: restartRouter } = useBioscopeRestartController_RestartRouter({
        onSettled: refetch,
    });

    const restarts = useMemo(
        () => restartsQuery.data?.data.map(toBioscopeRestart),
        [restartsQuery.data]
    );
    const routerRestarts = useMemo(
        () => routerRestartsQuery.data?.data.map(toBioscopeRouterRestart),
        [routerRestartsQuery.data]
    );

    return {
        restarts,
        routerRestarts,
        isRestartsLoading: restartsQuery.isPending,
        isRouterRestartsLoading: routerRestartsQuery.isPending,
        error: restartsQuery.error ?? routerRestartsQuery.error,
        isFetching: restartsQuery.isFetching || routerRestartsQuery.isFetching,
        refetch,
        restartBioscope: () => restartBioscope({ bioscopeId }),
        restartRouter: () => restartRouter({ bioscopeId }),
    };
}
